import { z } from 'zod';
import { DRIVE_SOURCE_KINDS, DRIVE_SOURCE_STATUSES, type DriveSourceKindValue, type DriveSourceStatusValue } from './drives.js';

export const MICROSOFT_TENANT_CONSENT_BROADCAST_CHANNEL = 'vivport:microsoft-tenant-consent';

export interface MicrosoftTenantConsentBroadcastMessage {
  type: 'vivport:microsoft-tenant-consent';
  status: 'success' | 'error';
  reason?: string;
}

export interface MicrosoftTenantSourceSafe {
  id: number;
  name: string;
  status: DriveSourceStatusValue;
  tenantId: string;
  tenantDisplayName: string | null;
  consentedAt: string;
  lastVerifiedAt: string | null;
  driveCount: number;
  createdByMemberId: number;
  createdAt: string;
  updatedAt: string;
}

export interface MicrosoftTenantConsentStartResult {
  authUrl: string;
}

export interface MicrosoftTenantListResult {
  sources: MicrosoftTenantSourceSafe[];
  applicationClientId: string;
}

export const MICROSOFT_TENANT_DELETE_NOTE =
  'The tenant link has been removed from Vivport. Consent itself stays in Azure until the tenant admin removes the enterprise application.';

export interface MicrosoftTenantDeleteResult {
  deleted: true;
  note: typeof MICROSOFT_TENANT_DELETE_NOTE;
}

const microsoftTenantConsentCallbackSuccessQuerySchema = z.object({
  admin_consent: z.literal('True'),
  tenant: z.string().trim().min(1),
  state: z.string().trim().min(1),
  error: z.undefined().optional(),
  error_description: z.undefined().optional(),
});

const microsoftTenantConsentCallbackErrorQuerySchema = z.object({
  error: z.string().trim().min(1),
  error_description: z.string().trim().min(1).optional(),
  state: z.string().trim().min(1).optional(),
  admin_consent: z.undefined().optional(),
  tenant: z.undefined().optional(),
});

export const microsoftTenantConsentCallbackQuerySchema = z.union([
  microsoftTenantConsentCallbackSuccessQuerySchema,
  microsoftTenantConsentCallbackErrorQuerySchema,
]);

export type MicrosoftTenantConsentCallbackQuery = z.infer<typeof microsoftTenantConsentCallbackQuerySchema>;

export interface RelaySafe {
  id: number;
  name: string;
  status: DriveSourceStatusValue;
  hostname: string | null;
  relayVersion: string | null;
  platform: string | null;
  lastSeenAt: string | null;
  pairedAt: string | null;
  online: boolean;
  pairingExpiresAt: string | null;
  createdByMemberId: number;
  createdAt: string;
  updatedAt: string;
}

export const relaySafeSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  status: z.enum(DRIVE_SOURCE_STATUSES),
  hostname: z.string().nullable(),
  relayVersion: z.string().nullable(),
  platform: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
  pairedAt: z.string().nullable(),
  online: z.boolean(),
  pairingExpiresAt: z.string().nullable(),
  createdByMemberId: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const relayListInputSchema = z.object({}).strict();

export interface RelayListResult {
  relays: RelaySafe[];
}

export const relayListOutputSchema = z.object({ relays: z.array(relaySafeSchema) });

export const relayCreateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(64),
  })
  .strict();
export type RelayCreateInput = z.infer<typeof relayCreateInputSchema>;

export const relayDownloadKindSchema = z.enum(['windows', 'linux', 'node']);
export type RelayDownloadKind = z.infer<typeof relayDownloadKindSchema>;

export interface RelayDownload {
  kind: RelayDownloadKind;
  label: string;
  filename: string;
  url: string;
  available: boolean;
}

export const relayDownloadSchema = z.object({
  kind: relayDownloadKindSchema,
  label: z.string(),
  filename: z.string(),
  url: z.string(),
  available: z.boolean(),
});

export interface RelayCommands {
  pair: string;
  share: string;
  start: string;
}

export const relayCommandsSchema = z.object({
  pair: z.string(),
  share: z.string(),
  start: z.string(),
});

export interface RelayCreateResult {
  relay: RelaySafe;
  pairingCode: string;
  commands: RelayCommands;
  downloads: RelayDownload[];
}

export const relayCreateOutputSchema = z.object({
  relay: relaySafeSchema,
  pairingCode: z.string(),
  commands: relayCommandsSchema,
  downloads: z.array(relayDownloadSchema),
});

export const relayRevokeInputSchema = z
  .object({
    relayId: z.number().int().positive(),
  })
  .strict();
export type RelayRevokeInput = z.infer<typeof relayRevokeInputSchema>;

export const relayPairInputSchema = z
  .object({
    code: z.string().trim().length(8),
    hostname: z.string().trim().min(1).max(255),
    relayVersion: z.string().trim().min(1).max(64),
    platform: z.string().trim().min(1).max(32).optional(),
  })
  .strict();
export type RelayPairInput = z.infer<typeof relayPairInputSchema>;

export interface RelayPairResult {
  relayId: number;
  secret: string;
  relayName: string;
  hubName: string;
}

export interface DriveSourceSummary {
  id: number;
  kind: DriveSourceKindValue;
  status: DriveSourceStatusValue;
  name: string;
  online: boolean;
  driveCount: number;
}

export interface DriveSourceListResult {
  sources: DriveSourceSummary[];
}

export const driveSourceSummarySchema = z.object({
  id: z.number().int(),
  kind: z.enum(DRIVE_SOURCE_KINDS),
  status: z.enum(DRIVE_SOURCE_STATUSES),
  name: z.string(),
  online: z.boolean(),
  driveCount: z.number().int(),
});

export const driveSourceListInputSchema = z.object({}).strict();

export const driveSourceListOutputSchema = z.object({ sources: z.array(driveSourceSummarySchema) });

export const GOOGLE_DRIVE_SOURCE_BROADCAST_CHANNEL = 'vivport:google-drive-source';

export interface GoogleDriveSourceBroadcastMessage {
  type: 'vivport:google-drive-source';
  status: 'success' | 'error';
  reason?: string;
}

export interface GoogleDriveSourceSafe {
  id: number;
  name: string;
  status: DriveSourceStatusValue;
  accountEmail: string;
  accountDisplayName: string | null;
  lastVerifiedAt: string | null;
  lastRefreshedAt: string | null;
  revokedReason: string | null;
  canWrite: boolean;
  createdByMemberId: number;
  createdAt: string;
  updatedAt: string;
}

export interface GoogleDriveSourceListResult {
  sources: GoogleDriveSourceSafe[];
}

export interface GoogleDriveSourceStartResult {
  authUrl: string;
}

export interface GoogleDriveSourceDeleteResult {
  deleted: true;
}

const googleDriveSourceCallbackSuccessQuerySchema = z.object({
  code: z.string().trim().min(1),
  state: z.string().trim().min(1),
  error: z.undefined().optional(),
});

const googleDriveSourceCallbackErrorQuerySchema = z.object({
  error: z.string().trim().min(1),
  state: z.string().trim().min(1).optional(),
  code: z.undefined().optional(),
});

export const googleDriveSourceCallbackQuerySchema = z.union([
  googleDriveSourceCallbackSuccessQuerySchema,
  googleDriveSourceCallbackErrorQuerySchema,
]);

export type GoogleDriveSourceCallbackQuery = z.infer<typeof googleDriveSourceCallbackQuerySchema>;
